import { defineStore } from 'pinia';

export type AppChromeTab = 'home' | 'trade' | 'fund' | 'assets' | 'center';

/** 显示 App 级 header + tabbar 的 Tab 页 */
export const TAB_ROUTES: Record<string, AppChromeTab> = {
  'pages/home/index': 'home',
  'pages/trade/index': 'trade',
  'pages/fund/index': 'fund',
  'pages/assets/index': 'assets',
};

/** 中间快捷菜单页：仍显示底栏，但不点亮两侧 Tab */
const CENTER_SHORTCUT_ROUTES = new Set([
  'pages/aiprediction/index',
  'pages/copy-trade/index',
  'pages/create-market/index',
]);

/** 快捷页里自带顶栏、隐藏 App 级 header 的路由 */
const NO_APP_HEADER_ROUTES = new Set([
  'pages/aiprediction/index',
  'pages/create-market/index',
]);

/** 非 Tab，但仍显示 App 级 header（无底栏） */
const HEADER_ONLY_ROUTES = new Set(['pages/rank/index']);

const TAB_PATHS = new Set(Object.keys(TAB_ROUTES));

const normalizeRoute = (routeOrUrl: string): string => {
  if (!routeOrUrl) return '';
  let path = routeOrUrl.trim();
  if (path.includes('#')) {
    path = path.split('#').pop() || path;
  }
  if (path.startsWith('/')) path = path.slice(1);
  path = path.split('?')[0].split('#')[0];
  if (path.endsWith('/')) path = path.slice(0, -1);
  return path;
};

/** H5 地址栏路由（hash 模式）；栈顶偶发与 hash 不一致时以此为准 */
const routeFromLocation = (): string => {
  if (typeof window === 'undefined') return '';
  const hash = window.location.hash || '';
  if (hash.startsWith('#/')) return normalizeRoute(hash.slice(1));
  if (hash.startsWith('#')) return normalizeRoute(hash.slice(1));
  const path = window.location.pathname || '';
  return path && path !== '/' ? normalizeRoute(path) : '';
};

export const useAppChromeStore = defineStore('appChrome', {
  state: () => ({
    /** 当前路由是否属于应显示 tabbar 的 Tab 页 */
    routeTabVisible: true as boolean,
    /** 当前路由是否显示 App 级 header */
    routeHeaderVisible: true as boolean,
    /**
     * @deprecated 弹层改用 z-index 盖住底栏，不再用引用计数藏 tabbar。
     * 保留字段以免旧调用报错；visible 已忽略此值。
     */
    overlayDepth: 0 as number,
    currentTab: 'home' as AppChromeTab,
    /** 中间菜单当前类型 */
    tradeType: 'prediction' as string,
  }),

  getters: {
    /** 实际是否显示 tabbar：仅看路由（非 Tab 页隐藏；弹层不隐藏） */
    visible: (state) => state.routeTabVisible,
    /** 实际是否渲染 App 级 header */
    headerVisible: (state) => state.routeHeaderVisible,
    isTabRoute: (state) => state.routeTabVisible,
  },

  actions: {
    setTab(tab: AppChromeTab) {
      this.currentTab = tab;
      this.routeTabVisible = true;
      // 四侧主 Tab 一律显示 header；center 由 syncFromRoute / 快捷路由决定
      if (tab !== 'center') {
        this.routeHeaderVisible = true;
      }
    },

    hide() {
      this.routeTabVisible = false;
      this.routeHeaderVisible = false;
    },

    show() {
      this.routeTabVisible = true;
    },

    /** @deprecated 弹层不再藏底栏 */
    beginOverlay() {
      /* no-op */
    },

    /** @deprecated 弹层不再藏底栏 */
    endOverlay() {
      /* no-op */
    },

    /** @deprecated 弹层不再藏底栏 */
    clearOverlay() {
      this.overlayDepth = 0;
    },

    /** 根据路由字符串同步 routeTabVisible + currentTab + header */
    syncFromRoute(routeOrUrl?: string | null) {
      let route = routeOrUrl ? normalizeRoute(routeOrUrl) : '';

      // H5：优先地址栏，避免 getCurrentPages 栈顶落后于 hash 时误藏 chrome
      if (!route) {
        route = routeFromLocation();
      }

      if (!route) {
        const pages = getCurrentPages();
        if (pages.length) {
          route = normalizeRoute(pages[pages.length - 1]?.route || '');
        }
      }

      if (!route) return;

      const tab = TAB_ROUTES[route];
      if (tab) {
        this.currentTab = tab;
        this.routeTabVisible = true;
        this.routeHeaderVisible = true;
        return;
      }

      if (CENTER_SHORTCUT_ROUTES.has(route)) {
        // 快捷页：底栏显示，两侧 Tab 不高亮；同步菜单选中项
        this.currentTab = 'center';
        this.routeTabVisible = true;
        this.routeHeaderVisible = !NO_APP_HEADER_ROUTES.has(route);
        if (route === 'pages/aiprediction/index') this.tradeType = 'prediction';
        else if (route === 'pages/copy-trade/index') this.tradeType = 'copyTrade';
        else if (route === 'pages/create-market/index') this.tradeType = 'createMarket';
        return;
      }

      if (HEADER_ONLY_ROUTES.has(route)) {
        this.routeHeaderVisible = true;
        this.routeTabVisible = false;
        return;
      }

      this.routeTabVisible = false;
      this.routeHeaderVisible = false;
    },

    isTabPath(routeOrUrl: string): boolean {
      const route = normalizeRoute(routeOrUrl);
      // 含中间快捷页（AI 预测 / 跟单 / 创建市场），与 green 快捷底栏一致
      return TAB_PATHS.has(route) || CENTER_SHORTCUT_ROUTES.has(route);
    },

    /** 四侧主 Tab（首页/交易/理财/资产）；不含合约/AI 等二级页与中间快捷页 */
    isMainTabbarPath(routeOrUrl?: string | null): boolean {
      let route = routeOrUrl ? normalizeRoute(routeOrUrl) : '';
      if (!route) route = routeFromLocation();
      if (!route) {
        const pages = getCurrentPages();
        if (pages.length) {
          route = normalizeRoute(pages[pages.length - 1]?.route || '');
        }
      }
      return !!route && TAB_PATHS.has(route);
    },

    /** 快捷页进入时强制露出底栏（清残留 overlay） */
    ensureCenterTabbar(routeOrUrl?: string | null) {
      this.clearOverlay();
      this.syncFromRoute(routeOrUrl);
      this.show();
    },
  },
});
